import React, { useMemo } from "react";
import DOMPurify from "dompurify";
import { ExternalLink, Download, BadgeCheck, Share2, Zap, Trophy, Flame, Eye, Film, TrendingUp, Wallet, ChevronDown } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis } from "recharts";
import { PlatformIcon } from "@/components/campaign/PlatformIcon";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function fmtCompact(n) {
  const num = Number(n || 0);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(num >= 10_000 ? 0 : 1)}K`;
  return String(Math.round(num));
}
function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

/* -------- Banner -------- */
export function NewBannerCard({ campaign, joined, onShare, onClose }) {
  const banner = campaign?.banner_image_data_url || campaign?.banner_url;
  const status = campaign?.status || "active";
  // Verified badge: use real campaign data (campaign.verified or streamer/creator_verified)
  const verified = !!(campaign?.verified ?? campaign?.streamer_verified ?? campaign?.creator_verified);
  return (
    <div
      data-testid="campaign-detail-banner-card"
      className="relative overflow-hidden rounded-3xl border border-[#1a1a1a] bg-[#0a0a0a]"
    >
      {banner ? (
        <div className="relative h-[180px] sm:h-[240px] lg:h-[280px] w-full">
          {/* Blurred backdrop fills the area so any aspect image looks framed */}
          <img
            aria-hidden="true"
            src={banner}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            style={{ filter: "blur(28px) saturate(140%) brightness(0.55)", transform: "scale(1.18)" }}
          />
          {/* Sharp contained foreground — full image always visible */}
          <img
            data-testid="campaign-detail-image"
            src={banner}
            alt=""
            className="absolute inset-0 mx-auto h-full w-full object-contain"
          />
          {/* vignette */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-black/40" />
        </div>
      ) : (
        <div className="h-[180px] sm:h-[240px] lg:h-[280px] w-full bg-gradient-to-br from-blue-700 via-blue-600 to-blue-900" />
      )}

      {/* TOP-LEFT: Status pill */}
      <div className="absolute left-4 top-4">
        <span
          data-testid="campaign-detail-status-badge"
          className="inline-flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1 text-[12px] font-semibold text-white backdrop-blur-md capitalize"
        >
          <span
            className={`h-2 w-2 rounded-full ${
              status === "active" || status === "available"
                ? "bg-emerald-400 animate-pulse"
                : status === "paused"
                ? "bg-amber-400"
                : "bg-rose-400"
            }`}
          />
          {status}
        </span>
      </div>

      {/* TOP-RIGHT: Share icon */}
      <button
        type="button"
        data-testid="campaign-detail-share-btn"
        onClick={onShare}
        title="Copy campaign link"
        className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white backdrop-blur-md transition-all hover:scale-105 hover:bg-black/75"
      >
        <Share2 size={15} />
      </button>

      {/* BOTTOM-LEFT: Avatar + title overlay */}
      <div className="absolute inset-x-4 bottom-4 flex items-center gap-3">
        <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border border-white/20 bg-black ring-2 ring-black/30">
          {(() => {
            const creatorAvatar = campaign?.streamer_image_data_url || campaign?.streamer_image_url || campaign?.creator_image_data_url || campaign?.creator_image_url || campaign?.logo_url || campaign?.brand_logo_url;
            return creatorAvatar ? (
              <img src={creatorAvatar} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm font-black text-[#c8ff00]">
                {(campaign?.creator_brand || campaign?.name || "C").slice(0, 2).toUpperCase()}
              </div>
            );
          })()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1
              data-testid="campaign-detail-title"
              className="truncate text-xl font-bold text-white sm:text-2xl"
            >
              {campaign?.name || "Campaign"}
            </h1>
            {verified && (
              <BadgeCheck size={18} className="shrink-0 fill-[#c8ff00] text-black" data-testid="campaign-verified-badge" />
            )}
          </div>
          {campaign?.creator_brand && (
            <div className="truncate text-xs text-white/70">{campaign.creator_brand}</div>
          )}
        </div>

        {onClose && (
          <button
            type="button"
            data-testid="campaign-detail-collapse-btn"
            onClick={onClose}
            className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/55 text-white backdrop-blur-md transition-all hover:scale-105 hover:bg-black/75"
            title="Close"
          >
            <ChevronDown size={16} />
          </button>
        )}
      </div>

      {/* Bonus chips just below status, only when present */}
      {(campaign?.bonus_rewards || []).length > 0 && (
        <div className="absolute left-4 top-14 flex flex-wrap gap-1.5">
          {(campaign.bonus_rewards || []).includes("viral") && (
            <span data-testid="campaign-detail-bonus-viral" className="inline-flex items-center gap-1 rounded-full bg-black/75 px-2 py-0.5 text-[10px] font-bold text-[#c8ff00] backdrop-blur">
              <Zap size={10} /> Viral
            </span>
          )}
          {(campaign.bonus_rewards || []).includes("leaderboard") && (
            <span data-testid="campaign-detail-bonus-rank" className="inline-flex items-center gap-1 rounded-full bg-black/75 px-2 py-0.5 text-[10px] font-bold text-amber-300 backdrop-blur">
              <Trophy size={10} /> Rank
            </span>
          )}
          {(campaign.bonus_rewards || []).includes("streak") && (
            <span data-testid="campaign-detail-bonus-streak" className="inline-flex items-center gap-1 rounded-full bg-black/75 px-2 py-0.5 text-[10px] font-bold text-orange-300 backdrop-blur">
              <Flame size={10} /> Streak
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* -------- Description Card -------- */
export function NewDescriptionCard({ campaign }) {
  return (
    <div data-testid="campaign-card-description" className="rounded-3xl border border-[#1a1a1a] bg-[#0a0a0a] p-5">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <Film size={14} className="text-[#c8ff00]" />
        Description
      </div>
      <div className="min-h-[160px] rounded-2xl border border-[#1a1a1a]/80 bg-black/40 p-4 text-sm leading-relaxed text-zinc-300">
        {campaign?.description_format === "html" ? (
          <div className="prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(campaign?.description || "") }} />
        ) : (
          <div className="whitespace-pre-wrap break-words">{campaign?.description || <span className="text-zinc-600">No description provided.</span>}</div>
        )}
      </div>
    </div>
  );
}

/* -------- References Card -------- */
export function NewReferencesCard({ campaign }) {
  const refs = (campaign?.tutorial_videos && campaign.tutorial_videos.length)
    ? campaign.tutorial_videos
    : (campaign?.tutorial_video_url
        ? [{ url: campaign.tutorial_video_url, name: campaign.tutorial_video_name || "Reference" }]
        : []);
  const visible = refs.slice(0, 3);
  const placeholders = Math.max(0, 3 - visible.length);
  return (
    <div data-testid="campaign-card-references" className="rounded-3xl border border-[#1a1a1a] bg-[#0a0a0a] p-5">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <Film size={14} className="text-[#c8ff00]" />
        Reference Videos &amp; Content
      </div>
      <div className="grid grid-cols-3 gap-3">
        {visible.map((vid, idx) => (
          <div key={vid.id || vid.url || idx} className="overflow-hidden rounded-2xl border border-[#1a1a1a]/80 bg-black">
            <video data-testid={`campaign-detail-reference-${idx}`} controls src={vid.url} className="aspect-[9/16] w-full bg-black object-cover" />
          </div>
        ))}
        {Array.from({ length: placeholders }).map((_, i) => (
          <div
            key={`empty-${i}`}
            data-testid={`campaign-detail-reference-empty-${i}`}
            className="flex aspect-[9/16] items-center justify-center rounded-2xl border border-dashed border-[#1a1a1a]/80 bg-black/40 text-[10px] text-zinc-700"
          >
            —
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------- Music & Branding Card -------- */
export function NewMusicBrandingCard({ campaign }) {
  const music = campaign?.music_branding || {};
  const watermark = campaign?.watermark_logo_url || campaign?.logo_url || campaign?.brand_logo_url;
  const hasMusic = music.url || music.title;
  const hasWatermark = !!watermark;
  if (!hasMusic && !hasWatermark) return null;

  return (
    <div data-testid="campaign-card-music-branding" className="rounded-3xl border border-[#1a1a1a] bg-[#0a0a0a] p-5">
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-white">
        <span className="text-[#c8ff00]">♪</span> Music &amp; Branding
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {hasMusic && (
          <a
            data-testid="campaign-detail-music-tile"
            href={music.url || "#"}
            target={music.url ? "_blank" : undefined}
            rel="noreferrer"
            className="flex items-center gap-3 rounded-2xl border border-[#1a1a1a]/80 bg-black/40 px-4 py-3 transition-colors hover:border-[#c8ff00]/30 hover:bg-[#c8ff00]/[0.04]"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black">
              <PlatformIcon platform={String(music.platform || "tiktok").toLowerCase()} size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-white">{music.title || "Use this sound"}</div>
              <div className="text-[11px] font-semibold text-[#c8ff00]/80">Must use exact sound</div>
            </div>
            <ExternalLink size={16} className="shrink-0 text-[#c8ff00]" />
          </a>
        )}
        {hasWatermark && (
          <a
            data-testid="campaign-detail-watermark-tile"
            href={watermark}
            target="_blank"
            rel="noreferrer"
            download
            className="flex items-center gap-3 rounded-2xl border border-[#1a1a1a]/80 bg-black/40 px-4 py-3 transition-colors hover:border-[#c8ff00]/30 hover:bg-[#c8ff00]/[0.04]"
          >
            <div className="flex h-9 w-9 shrink-0 overflow-hidden rounded-full bg-black">
              <img src={watermark} alt="" className="h-full w-full object-cover" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-bold text-white">Watermark Logo</div>
              <div className="text-[11px] font-semibold text-[#c8ff00]/80">Must use exact Logo</div>
            </div>
            <Download size={16} className="shrink-0 text-[#c8ff00]" />
          </a>
        )}
      </div>
    </div>
  );
}

/* -------- Rules Footer -------- */
export function NewRulesFooter() {
  return (
    <div data-testid="campaign-detail-rules-footer" className="px-3 py-4 text-center text-xs text-zinc-500">
      By participating in our campaigns, you agree to{" "}
      <a href="/rules" className="font-semibold text-[#c8ff00] hover:underline">ClippingBase</a>{" "}Rules
    </div>
  );
}

/* -------- Stats Panel -------- */
export function NewStatsPanel({ campaign, progress, totalViews, totalSubmissions, summaryTrend }) {
  const budgetTotal = Number(campaign?.budget_total || 0);
  const budgetSpent = Number(progress?.spent ?? progress?.budget_spent ?? 0);
  const budgetPct = budgetTotal > 0 ? Math.min((budgetSpent / budgetTotal) * 100, 100) : 0;

  // Trend label like "Jun 20 - Jun 27"
  const trendRange = useMemo(() => {
    if (!summaryTrend?.length) return "";
    return `${summaryTrend[0].date} – ${summaryTrend[summaryTrend.length - 1].date}`;
  }, [summaryTrend]);

  const trendDelta = useMemo(() => {
    if (!summaryTrend?.length) return 0;
    return summaryTrend.reduce((a, p) => a + Number(p.views || 0), 0);
  }, [summaryTrend]);

  return (
    <div data-testid="campaign-stats-panel" className="rounded-3xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
      {/* Top: 2 stat cards */}
      <div className="grid grid-cols-2 gap-3">
        <div data-testid="campaign-stats-total-views" className="rounded-2xl border border-[#1a1a1a]/80 bg-black/40 p-3">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#c8ff00]">
            <Eye size={11} /> Total Views
          </div>
          <div className="mt-2 text-2xl font-black text-[#c8ff00] tabular-nums">{fmtCompact(totalViews)}</div>
        </div>
        <div data-testid="campaign-stats-submissions" className="rounded-2xl border border-[#1a1a1a]/80 bg-black/40 p-3">
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#c8ff00]">
            <Film size={11} /> Submissions
          </div>
          <div className="mt-2 text-2xl font-black text-white tabular-nums">{Number(totalSubmissions || 0).toLocaleString("en-US")}</div>
        </div>
      </div>

      {/* Trend */}
      <div className="mt-4">
        <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold text-zinc-400">
          <TrendingUp size={12} className="text-[#c8ff00]" /> Views Trend
        </div>
        <div className="h-[120px]">
          {summaryTrend?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={summaryTrend} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="statsTrendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <Area type="natural" dataKey="views" stroke="#22c55e" strokeWidth={2.5} fill="url(#statsTrendFill)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-[#1a1a1a] text-[11px] text-zinc-600">No trend data yet</div>
          )}
        </div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-zinc-400">
          <span>{trendRange || "—"}</span>
          <span className="font-semibold text-zinc-300">+{fmtCompact(trendDelta)} Views</span>
        </div>
      </div>

      {/* Budget */}
      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between text-[12px]">
          <span className="font-semibold text-zinc-300">Budget Usage</span>
          <span className="font-bold text-white tabular-nums">{budgetPct.toFixed(0)}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-emerald-400 transition-[width] duration-500" style={{ width: `${budgetPct}%` }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-400">
          <span className="inline-flex items-center gap-1">
            <Wallet size={11} className="text-zinc-300" />
            <span className="font-semibold text-white tabular-nums">{fmtCompact(budgetTotal)}</span>
            <span className="ml-0.5 text-zinc-500">total budget</span>
          </span>
          <span className="inline-flex items-center gap-1 text-emerald-400">
            <span className="font-bold tabular-nums">${Number(budgetSpent).toLocaleString("en-US")}</span>
            <span className="text-emerald-400/70">spent</span>
          </span>
        </div>
      </div>
    </div>
  );
}

/* -------- Leaderboard list (top-5 styled) -------- */
export function NewLeaderboardList({ leaderboard }) {
  if (!leaderboard?.length) {
    return (
      <div data-testid="campaign-leaderboard-empty" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] px-4 py-10 text-center text-sm text-zinc-500">
        No data yet for this campaign.
      </div>
    );
  }
  const rankBg = {
    1: "linear-gradient(90deg, rgba(202,138,4,0.45) 0%, rgba(202,138,4,0.18) 100%)",
    2: "linear-gradient(90deg, rgba(161,161,170,0.45) 0%, rgba(161,161,170,0.18) 100%)",
    3: "linear-gradient(90deg, rgba(249,115,22,0.55) 0%, rgba(249,115,22,0.18) 100%)",
  };
  return (
    <div className="space-y-2">
      {leaderboard.slice(0, 10).map((entry) => (
        <div
          key={entry.user_id}
          data-testid={`campaign-leaderboard-row-${entry.rank}`}
          className="flex items-center gap-3 rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] px-3 py-2.5"
          style={rankBg[entry.rank] ? { background: rankBg[entry.rank], borderColor: "transparent" } : undefined}
        >
          <div className="w-7 shrink-0 text-center text-sm font-black text-white">#{entry.rank}</div>
          <img
            src={entry.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${entry.username}`}
            alt=""
            className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-white/10"
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold text-white">{entry.display_name || entry.username}</div>
            <div className="truncate text-[11px] text-white/70">@{entry.username}</div>
          </div>
          <div className="flex flex-col items-end gap-0.5 text-right">
            <div className="inline-flex items-center gap-2 text-[11px] text-white/80">
              <span className="inline-flex items-center gap-1"><Film size={10} className="text-[#c8ff00]" /> {entry.clip_count || 1}</span>
              <span className="inline-flex items-center gap-1"><Eye size={10} className="text-[#c8ff00]" /> {fmtCompact(entry.total_views)}</span>
            </div>
            <div className="text-sm font-black text-white tabular-nums">{fmtMoney(entry.earned)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------- Tabs primitive used by both desktop sidebar and mobile column -------- */
export function NewTabsBar({ tab, setTab, includeStats = false, testIdPrefix = "campaign" }) {
  const triggers = includeStats
    ? [
        { value: "details", label: "Details" },
        { value: "submit", label: "Submit" },
        { value: "leaderboard", label: "Leaderboard" },
        { value: "stats", label: "Stats" },
      ]
    : [
        { value: "details", label: "Details" },
        { value: "submit", label: "Submit" },
        { value: "leaderboard", label: "Leaderboard" },
      ];
  return (
    <TabsList
      data-testid={`${testIdPrefix}-new-tabs`}
      className={`grid w-full ${triggers.length === 4 ? "grid-cols-4" : "grid-cols-3"} border border-[#1a1a1a] bg-black/60 p-1 backdrop-blur-xl gap-1 rounded-2xl`}
    >
      {triggers.map((t) => (
        <TabsTrigger
          key={t.value}
          value={t.value}
          data-testid={`${testIdPrefix}-new-tab-${t.value}`}
          className="text-xs sm:text-sm whitespace-nowrap rounded-xl data-[state=active]:bg-[#c8ff00] data-[state=active]:text-black"
        >
          {t.label}
        </TabsTrigger>
      ))}
    </TabsList>
  );
}
